import { useEffect, useState } from 'react'
import { Image, Pressable, Text, View } from 'react-native'
import { Globe, Image as ImageIcon, X } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as ImagePicker from 'expo-image-picker'
import { Button, InlineAlert, TextField, ToggleSwitch } from '@chillout/ui'
import { colors } from '@chillout/core'
import { reportUnexpectedError } from '../../../../lib/reportError'
import { AdminModal, ModalActions } from '../Modal'
import { SelectField } from '../../../shared/SelectField'
import type { SelectFieldOption } from '../../../shared/SelectField'
import { SUPPORTED_LOCALES } from '../../../../i18n'
import type { SupportedLocale } from '../../../../i18n'
import { rewardItemSchema, REWARD_ITEM_NAME_MAX, REWARD_ITEM_DESCRIPTION_MAX } from './rewardItemSchema'
import type { RewardItem, SaveRewardItemInput } from './types'

interface RewardItemFormValues {
  name: Record<SupportedLocale, string>
  description: Record<SupportedLocale, string>
  imageUrl: string | null
  active: boolean
}

const EMPTY_VALUES: RewardItemFormValues = {
  name: { en: '', sv: '' },
  description: { en: '', sv: '' },
  imageUrl: null,
  active: true,
}

async function pickImage(onChange: (uri: string) => void) {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    quality: 0.8,
  })
  if (!result.canceled && result.assets[0]) {
    onChange(result.assets[0].uri)
  }
}

interface RewardItemModalProps {
  i18nPrefix: string
  item: RewardItem | 'new' | null
  isAssigned: boolean
  isSaving: boolean
  isSaveError: boolean
  onSave: (input: SaveRewardItemInput) => Promise<unknown>
  onClose: () => void
}

export function RewardItemModal({ i18nPrefix, item, isAssigned, isSaving, isSaveError, onSave, onClose }: RewardItemModalProps) {
  const { t } = useTranslation()

  // Frozen to the last non-null item so content doesn't reshape while the
  // modal fades out after `item` is cleared to null on close.
  const [displayItem, setDisplayItem] = useState(item)
  const [displayIsAssigned, setDisplayIsAssigned] = useState(isAssigned)

  useEffect(() => {
    if (item === null) return
    setDisplayItem(item)
    setDisplayIsAssigned(isAssigned)
  }, [item, isAssigned])

  const isNew = displayItem === 'new'

  const langOptions: SelectFieldOption[] = [
    { label: t(`${i18nPrefix}.modal.langEnglish`), value: 'en' },
    { label: t(`${i18nPrefix}.modal.langSwedish`), value: 'sv' },
  ]

  const localeNames: Record<SupportedLocale, string> = {
    en: t(`${i18nPrefix}.modal.langEnglish`),
    sv: t(`${i18nPrefix}.modal.langSwedish`),
  }

  const [selectedLang, setSelectedLang] = useState<SupportedLocale>('en')

  const { control, handleSubmit, reset, watch, formState } = useForm<RewardItemFormValues>({
    resolver: zodResolver(rewardItemSchema(t, i18nPrefix)),
    defaultValues: EMPTY_VALUES,
    mode: 'onChange',
  })

  useEffect(() => {
    if (item === null) return
    reset(item !== 'new' ? { name: item.name, description: item.description, imageUrl: item.imageUrl, active: item.active } : EMPTY_VALUES)
    setSelectedLang('en')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, reset])

  const names = watch('name')
  const completeCount = SUPPORTED_LOCALES.filter((l) => names[l].trim().length > 0).length
  const incompleteLocales = SUPPORTED_LOCALES.filter((l) => names[l].trim().length === 0)
  const currentLangName = localeNames[selectedLang]

  const onSubmit = handleSubmit(async (values) => {
    try {
      await onSave({
        id: isNew ? null : (displayItem as RewardItem).id,
        name: values.name,
        description: values.description,
        imageUri: values.imageUrl,
        active: values.active,
      })
      onClose()
    } catch {
      // isSaveError reflects the failure via props
    }
  })

  return (
    <AdminModal
      visible={item !== null}
      onClose={onClose}
      closeAccessibilityLabel={t(`${i18nPrefix}.modal.closeAccessibilityLabel`)}
      title={isNew ? t(`${i18nPrefix}.modal.addTitle`) : t(`${i18nPrefix}.modal.editTitle`)}
      actions={
        <ModalActions>
          <Button label={t('common.cancel')} variant="secondary" className="flex-1" onPress={onClose} />
          <Button
            label={t(`${i18nPrefix}.modal.save`)}
            loadingLabel={t(`${i18nPrefix}.modal.saving`)}
            className="flex-1"
            onPress={onSubmit}
            disabled={!formState.isValid}
            loading={isSaving}
          />
        </ModalActions>
      }
    >
      <View className="mt-5 gap-4">
        {isSaveError ? <InlineAlert message={t(`${i18nPrefix}.modal.saveError`)} /> : null}

        <Controller
          control={control}
          name="imageUrl"
          render={({ field }) =>
            field.value ? (
              <Pressable
                onPress={() => { pickImage(field.onChange).catch(reportUnexpectedError) }}
                accessibilityRole="button"
                accessibilityLabel={t(`${i18nPrefix}.modal.changeImage`)}
                className="relative h-40 w-full overflow-hidden rounded-lg border border-border"
              >
                <Image source={{ uri: field.value }} resizeMode="cover" style={{ width: '100%', height: '100%' }} />
                <Pressable
                  onPress={() => field.onChange(null)}
                  accessibilityRole="button"
                  accessibilityLabel={t(`${i18nPrefix}.modal.removeImage`)}
                  className="absolute right-2 top-2 h-7 w-7 items-center justify-center rounded-full bg-canvas-card/90"
                >
                  <X size={16} color={colors.text.primary} strokeWidth={2} />
                </Pressable>
              </Pressable>
            ) : (
              <Pressable
                onPress={() => { pickImage(field.onChange).catch(reportUnexpectedError) }}
                accessibilityRole="button"
                accessibilityLabel={t(`${i18nPrefix}.modal.uploadImage`)}
                className="h-40 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-canvas-primary"
              >
                <ImageIcon size={24} color={colors.text.muted} strokeWidth={1.5} />
                <Text className="text-sm text-secondary">{t(`${i18nPrefix}.modal.uploadImage`)}</Text>
                <Text className="text-xs text-muted">{t(`${i18nPrefix}.modal.uploadImageHint`)}</Text>
              </Pressable>
            )
          }
        />

        <SelectField
          label={t(`${i18nPrefix}.modal.contentLanguage`)}
          value={selectedLang}
          options={langOptions}
          leftIcon={Globe}
          accessibilityLabel={t(`${i18nPrefix}.modal.contentLanguage`)}
          onValueChange={(v) => setSelectedLang(v as SupportedLocale)}
        />

        {incompleteLocales.length > 0 ? (
          <View className="flex-row items-start justify-between gap-3 rounded-lg border border-border bg-canvas-primary px-4 py-3">
            <View className="gap-0.5">
              <Text className="text-xs font-semibold text-primary">{t(`${i18nPrefix}.modal.translationsTitle`)}</Text>
              <Text className="text-xs text-secondary">{t(`${i18nPrefix}.modal.translationsStatus`, { count: completeCount, total: SUPPORTED_LOCALES.length })}</Text>
            </View>
            <View className="items-end gap-1">
              {incompleteLocales.map((locale) => (
                <Text key={locale} className="text-xs text-warning">{t(`${i18nPrefix}.modal.langIncomplete`, { language: localeNames[locale] })}</Text>
              ))}
            </View>
          </View>
        ) : null}

        <Controller
          control={control}
          name={`name.${selectedLang}`}
          render={({ field, fieldState }) => (
            <TextField
              label={`${t(`${i18nPrefix}.modal.nameLabel`)} (${currentLangName})`}
              value={field.value}
              onChangeText={field.onChange}
              onBlur={field.onBlur}
              required
              maxLength={REWARD_ITEM_NAME_MAX}
              error={
                fieldState.error?.message ??
                (field.value.trim().length === 0
                  ? t(`${i18nPrefix}.modal.nameIncomplete`, { language: currentLangName })
                  : undefined)
              }
              accessibilityLabel={`${t(`${i18nPrefix}.modal.nameLabel`)} ${currentLangName}`}
            />
          )}
        />

        <Controller
          control={control}
          name={`description.${selectedLang}`}
          render={({ field }) => (
            <TextField
              label={`${t(`${i18nPrefix}.modal.descriptionLabel`)} (${currentLangName})`}
              value={field.value}
              onChangeText={field.onChange}
              onBlur={field.onBlur}
              multiline
              maxLength={REWARD_ITEM_DESCRIPTION_MAX}
              accessibilityLabel={`${t(`${i18nPrefix}.modal.descriptionLabel`)} ${currentLangName}`}
            />
          )}
        />

        {!isNew ? (
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <View className="gap-1">
                <View className="flex-row items-center justify-between">
                  <Text className="text-sm font-semibold text-primary">{t(`${i18nPrefix}.modal.activeLabel`)}</Text>
                  <ToggleSwitch
                    value={field.value}
                    disabled={displayIsAssigned}
                    onValueChange={field.onChange}
                    accessibilityLabel={t(`${i18nPrefix}.modal.activeLabel`)}
                  />
                </View>
                {displayIsAssigned ? (
                  <Text className="text-xs text-secondary">{t(`${i18nPrefix}.modal.activeLockedHint`)}</Text>
                ) : null}
              </View>
            )}
          />
        ) : null}
      </View>
    </AdminModal>
  )
}
